import React, { useState } from "react";
import SectionBezel from "components/SectionBezel";
import Capture from "./Capture";
import SetupFutureUsage from "./SetupFutureUsage";
import Installments from "./Installments";
import Methods from "./Methods";
import Mode from "./Mode";
import compact from "lodash/compact";
import CheckoutAttributeToggle from "components/CheckoutAttributeToggle";
import { useUserFeature } from "utils/hooks/capabilities";
import { USER_CAPABILITY } from "utils/constants/capability";
import { SHORT_LINK_SETTINGS } from "utils/constants/shortLink";
import { getFieldNameRoot } from "utils/form";
import { CHECKOUT_SESSION } from "utils/constants/stripe";
import { useShortLinkSubscriptionSchedule } from "utils/hooks/shortLink";
import {
  getCheckoutAttributeConfig,
  getCheckoutAttributesConfig
} from "utils/checkout/attributes";
import { SUBSCRIPTION_SCHEDULE } from "utils/constants/state";

const Payment = ({
  name,
  inherited,
  fieldset = [
    SHORT_LINK_SETTINGS.PAYMENT_METHODS,
    SHORT_LINK_SETTINGS.SUBSCRIPTION_SCHEDULE,
    SHORT_LINK_SETTINGS.CAPTURE_METHOD,
    SHORT_LINK_SETTINGS.SETUP_FUTURE_USAGE
  ]
}) => {
  const [open, setOpen] = useState(false);
  const sharedProps = {
    name,
    inherited
  };
  const installmentsEnabled = useUserFeature(USER_CAPABILITY.INSTALLMENTS);

  const {
    disabled: disableSubscriptionSchedule
  } = useShortLinkSubscriptionSchedule();

  const fieldNameRoot = getFieldNameRoot(name);
  const hasPaymentModeControls =
    fieldset.indexOf(SHORT_LINK_SETTINGS.PAYMENT_MODE) >= 0;
  const hasPaymentMethodTypeControls =
    fieldset.indexOf(SHORT_LINK_SETTINGS.PAYMENT_METHODS) >= 0;
  const hasSetupFutureUsageControls =
    fieldset.indexOf(SHORT_LINK_SETTINGS.SETUP_FUTURE_USAGE) >= 0;
  const hasCaptureMethodControls =
    fieldset.indexOf(SHORT_LINK_SETTINGS.CAPTURE_METHOD) >= 0;
  const hasSubscriptionScheduleControls =
    installmentsEnabled &&
    fieldset.indexOf(SHORT_LINK_SETTINGS.SUBSCRIPTION_SCHEDULE) >= 0;

  const attributes = [];
  if (hasPaymentModeControls) {
    attributes.push(CHECKOUT_SESSION.MODE);
  }
  if (hasPaymentMethodTypeControls) {
    attributes.push(CHECKOUT_SESSION.PAYMENT_METHOD_TYPES);
  }
  if (hasSetupFutureUsageControls || hasCaptureMethodControls) {
    attributes.push(CHECKOUT_SESSION.PAYMENT_INTENT_DATA);
  }

  const attributesConfig = [
    ...getCheckoutAttributesConfig({
      attributes,
      name
    })
  ];

  if (hasSubscriptionScheduleControls) {
    attributesConfig.push(
      getCheckoutAttributeConfig({
        attribute: SUBSCRIPTION_SCHEDULE,
        path: `${fieldNameRoot}.${SUBSCRIPTION_SCHEDULE}`
      })
    );
  }

  const toggleProps = {
    name,
    attributes: attributesConfig,
    isOpen: open,
    onChange: (updatedOpen) => updatedOpen && setOpen(updatedOpen)
  };

  if (
    hasSubscriptionScheduleControls &&
    attributesConfig.length === 1 &&
    disableSubscriptionSchedule
  ) {
    toggleProps.disabled = true;
  }

  const nodes = compact([
    hasPaymentMethodTypeControls && <Methods {...sharedProps} />,
    hasPaymentModeControls && <Mode {...sharedProps} />,
    hasSetupFutureUsageControls && <SetupFutureUsage {...sharedProps} />,
    hasCaptureMethodControls && <Capture {...sharedProps} />,
    hasSubscriptionScheduleControls && installmentsEnabled && (
      <Installments {...sharedProps} />
    )
  ]);

  return nodes.length ? (
    <SectionBezel
      header="Payment"
      expandable
      action={<CheckoutAttributeToggle {...toggleProps} />}
      isOpen={open}
      setOpen={setOpen}
    >
      {nodes.map((node, nodeIx) => (
        <div
          key={nodeIx}
          className={nodeIx ? "pt3 mt2 w-100 bt hairline-1" : ""}
        >
          {node}
        </div>
      ))}
    </SectionBezel>
  ) : null;
};

export default Payment;
